import {Injectable} from '@angular/core';
import {ActivatedRouteSnapshot} from '@angular/router';
import {ErrorNotifierService} from 'app/core/error-handler/error-notifier.service';
import {InternalError} from 'app/core/error-handler/errors/internal-error';
import {AuthService} from 'app/core/services/auth.service';
import {RouterHelperService} from 'app/core/services/router-helper.service';
import {ApiService} from 'app/shared/modules/api/services/api.service';
import {BehaviorSubject, Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {supportedNetworksFields} from '../components/network-account-form/supported-networks.fields';
import {NetworkAccount, Networks} from '../interfaces/networks';
import {listNetworksQuery} from './list-networks.query';
import {MediationEngine} from 'src/globalTypes';

@Injectable()
export class LinkedNetworksResolver {
    networks$: Observable<Networks[]> = new BehaviorSubject([]);

    userId: string;

    queryRef;

    get permissions() {
        return this.auth.getCurrentUserLastValue()?.permissions;
    }

    constructor(
        private auth: AuthService,
        private api: ApiService,
        private errorNotifier: ErrorNotifierService,
        private routerHelper: RouterHelperService
    ) {}

    private isSupported = (network: Networks) => {
        if (!supportedNetworksFields[network.accountTypeName]) {
            this.errorNotifier.notifyBugTracker(new InternalError(`Unrecognized network type  '${network.accountTypeName}'.`));
        }
        return supportedNetworksFields[network.accountTypeName];
    };

    getUserId() {
        return this.userId;
    }

    getQueryRef(userId: string, mediationEngine?: MediationEngine) {
        return this.api.watchQuery({
            query: listNetworksQuery,
            variables: {userId, ...(mediationEngine && {monetizationEngine: mediationEngine})},
        });
    }

    resolve(route: ActivatedRouteSnapshot) {
        this.userId = route.queryParams['user_id'] || this.auth.currentUserId;
        return this.fetchNetworks(this.userId);
    }

    fetchNetworks(userId: string, mediationEngine?: MediationEngine) {
        this.queryRef = this.getQueryRef(userId, mediationEngine);

        const data$ = <Observable<Networks[]>>this.queryRef.valueChanges.pipe(
            map(({listNetworks, listUserNetworkAccounts}: {listNetworks: Networks[]; listUserNetworkAccounts: NetworkAccount[]}) => {
                const defaultAccountsMap = new Map<any, NetworkAccount>(
                    <[string, NetworkAccount][]>listUserNetworkAccounts.filter(account => account.isDefault).map(v => [v.networkId, v])
                );
                return listNetworks.filter(this.isSupported).map(network => {
                    const defaultAccount = defaultAccountsMap.get(network.id);
                    return !defaultAccount
                        ? network
                        : {
                              ...network,
                              defaultAccountLogin: defaultAccount.login,
                          };
                });
            })
        );
        return this.queryRef
            .result()
            .then(() => {
                this.networks$ = data$;
            })
            .catch(err => {
                this.networks$ = new BehaviorSubject([]);
                this.routerHelper.resolverErrorHandler(err);
            });
    }
}
